import { useMemo } from 'react';
import { Link2, Zap, ArrowRightLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface ResourceHint {
  type: 'preload' | 'prefetch' | 'preconnect' | 'dns-prefetch' | 'prerender';
  url: string;
  entry: HAREntry;
  as?: string;
  crossorigin?: string;
}

interface UsedHint {
  hint: ResourceHint;
  usedBy: HAREntry;
  timing: 'early' | 'on-time' | 'late';
}

interface UnusedHint {
  hint: ResourceHint;
  reason: string;
}

export function ResourceHints() {
  const { filteredEntries, selectEntry } = useHARStore();

  const { hints, usedHints, unusedHints, insights } = useMemo(() => {
    const hints: ResourceHint[] = [];
    const usedByMap: Map<string, HAREntry> = new Map();

    // Find all resource hints from link headers and HTML
    filteredEntries.forEach(entry => {
      // Check Link headers
      const linkHeaders = entry.response.headers.filter(h => 
        h.name.toLowerCase() === 'link'
      );

      linkHeaders.forEach(header => {
        const relMatch = header.value.match(/rel=(?:"|')?(preload|prefetch|preconnect|dns-prefetch|prerender)(?:"|')?/i);
        const urlMatch = header.value.match(/<([^>]+)>/);
        
        if (relMatch && urlMatch) {
          const asMatch = header.value.match(/as=(?:"|')?([^"';\s]+)(?:"|')?/i);
          const crossoriginMatch = header.value.match(/crossorigin(?:=(?:"|')?([^"';\s]*)(?:"|')?)?/i);
          
          // Resolve relative URLs
          let url = urlMatch[1];
          try {
            url = new URL(url, entry.request.url).href;
          } catch {
            // Keep as-is if not valid
          }

          hints.push({
            type: relMatch[1].toLowerCase() as ResourceHint['type'],
            url,
            entry,
            as: asMatch?.[1],
            crossorigin: crossoriginMatch?.[1] || (crossoriginMatch ? '' : undefined),
          });
        }
      });

      // Check for resources that might be requested later
      const domain = new URL(entry.request.url).hostname;
      usedByMap.set(entry.request.url, entry);
      usedByMap.set(domain, entry);
    });

    // Analyze which hints were actually used
    const usedHints: UsedHint[] = [];
    const unusedHints: UnusedHint[] = [];

    hints.forEach(hint => {
      const usedEntry = usedByMap.get(hint.url);
      
      if (usedEntry) {
        const hintTime = new Date(hint.entry.startedDateTime).getTime();
        const usedTime = new Date(usedEntry.startedDateTime).getTime();
        const timeDiff = usedTime - hintTime;

        let timing: UsedHint['timing'];
        if (timeDiff < 0) {
          timing = 'late';
        } else if (timeDiff > 5000) {
          timing = 'early';
        } else {
          timing = 'on-time';
        }

        usedHints.push({ hint, usedBy: usedEntry, timing });
      } else {
        unusedHints.push({
          hint,
          reason: 'Resource was never requested',
        });
      }
    });

    // Generate insights
    const insights: { type: 'success' | 'warning' | 'info'; message: string; count?: number }[] = [];

    const preloadHints = hints.filter(h => h.type === 'preload');
    if (preloadHints.length > 0) {
      const usedPreloads = usedHints.filter(h => h.hint.type === 'preload');
      const usageRate = (usedPreloads.length / preloadHints.length) * 100;
      
      if (usageRate >= 80) {
        insights.push({
          type: 'success',
          message: `${Math.round(usageRate)}% of preload hints were utilized`,
          count: usedPreloads.length,
        });
      } else if (usageRate < 50) {
        insights.push({
          type: 'warning',
          message: `Only ${Math.round(usageRate)}% of preload hints were used. Consider removing unused hints.`,
          count: preloadHints.length - usedPreloads.length,
        });
      }
    }

    const dnsPrefetchHints = hints.filter(h => h.type === 'dns-prefetch');
    if (dnsPrefetchHints.length > 3) {
      insights.push({
        type: 'info',
        message: `Multiple DNS prefetches detected (${dnsPrefetchHints.length}). Ensure these are for critical third-party domains.`,
      });
    }

    if (unusedHints.length > 5) {
      insights.push({
        type: 'warning',
        message: `${unusedHints.length} resource hints were unused, wasting bandwidth and CPU`,
        count: unusedHints.length,
      });
    }

    return { hints, usedHints, unusedHints, insights };
  }, [filteredEntries]);

  if (hints.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <Link2 className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No resource hints found</h3>
        <p className="text-xs text-slate-500">This HAR doesn't contain any preload, prefetch, or other resource hints.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 space-y-4">
        {/* Insights */}
        {insights.length > 0 && (
          <div className="space-y-2">
            {insights.map((insight, index) => (
              <div
                key={index}
                className={cn(
                  'p-3 rounded-lg border',
                  insight.type === 'success' && 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30',
                  insight.type === 'warning' && 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30',
                  insight.type === 'info' && 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'p-1.5 rounded',
                    insight.type === 'success' && 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400',
                    insight.type === 'warning' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                    insight.type === 'info' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                  )}>
                    {insight.type === 'success' && <CheckCircle2 className="w-4 h-4" />}
                    {insight.type === 'warning' && <AlertTriangle className="w-4 h-4" />}
                    {insight.type === 'info' && <Zap className="w-4 h-4" />}
                  </div>
                  <div className="flex-1">
                    <p className={cn(
                      'text-sm',
                      insight.type === 'success' && 'text-green-700 dark:text-green-400',
                      insight.type === 'warning' && 'text-yellow-700 dark:text-yellow-400',
                      insight.type === 'info' && 'text-blue-700 dark:text-blue-400'
                    )}>
                      {insight.message}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Used Hints */}
        {usedHints.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Used Hints ({usedHints.length})
            </h3>
            {usedHints.slice(0, 5).map((item, index) => (
              <div
                key={index}
                className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded uppercase font-bold',
                    item.hint.type === 'preload' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
                    item.hint.type === 'prefetch' && 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300',
                    item.hint.type === 'preconnect' && 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
                    item.hint.type === 'dns-prefetch' && 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300',
                    item.hint.type === 'prerender' && 'bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-300'
                  )}>
                    {item.hint.type}
                  </span>
                  {item.hint.as && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                      as: {item.hint.as}
                    </span>
                  )}
                  <span className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded ml-auto',
                    item.timing === 'on-time' && 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
                    item.timing === 'early' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
                    item.timing === 'late' && 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                  )}>
                    {item.timing === 'on-time' && 'On time'}
                    {item.timing === 'early' && 'Too early'}
                    {item.timing === 'late' && 'Too late'}
                  </span>
                </div>
                <div className="text-xs text-slate-700 dark:text-slate-300 truncate mb-1">
                  {getFileName(item.hint.url)}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  <button
                    onClick={() => selectEntry(item.hint.entry)}
                    className="hover:text-slate-700 dark:hover:text-slate-300 underline"
                  >
                    Hint source
                  </button>
                  <ArrowRightLeft className="w-3 h-3" />
                  <button
                    onClick={() => selectEntry(item.usedBy)}
                    className="hover:text-slate-700 dark:hover:text-slate-300 underline"
                  >
                    Used by
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Unused Hints */}
        {unusedHints.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Unused Hints ({unusedHints.length})
            </h3>
            {unusedHints.slice(0, 3).map((item, index) => (
              <div
                key={index}
                className="p-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded uppercase font-bold',
                    item.hint.type === 'preload' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
                    item.hint.type === 'prefetch' && 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300'
                  )}>
                    {item.hint.type}
                  </span>
                  <AlertTriangle className="w-3 h-3 text-red-500" />
                </div>
                <div className="text-xs text-slate-700 dark:text-slate-300 truncate">
                  {getFileName(item.hint.url)}
                </div>
                <div className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                  {item.reason}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
